import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: Convert E844SD rows to E844D records.
 *
 * Runs after E844SD import + classify. For each recently-imported E844SD row:
 * 1. Enriches with reference data (E844V, ITEMAST, PCOVDET, E844P)
 * 2. Converts to E844D via OPE84412 (convertE844SDToE844D)
 * 3. Applies OPE84440 pricing recalculation
 * 4. Persists to sd_e844d table
 */
export const convertE844sdToE844dStep = defineStepConfig({
  type: "convert_e844sd_to_e844d",
  name: "Convert E844SD to E844D",
  description:
    "Converts E844SD rows to E844D records (Option 1: OPE84412 + OPE84440). " +
    "Enriches with reference data, converts 123 columns to 91 E844D columns, " +
    "recalculates pricing, and persists to sd_e844d.",
  category: "Transformation",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process E844SD rows updated after this time. " +
          "Typically wired from the bulkInsert step's startedAt output.",
      ),
    company: z
      .string()
      .optional()
      .describe(
        "Restrict conversion to this company code. Wire from the bulkInsert " +
          "step's company output so a fanout child converts only its own slice.",
      ),
    division: z
      .string()
      .optional()
      .describe(
        "Restrict conversion to this division code. Wire from the bulkInsert " +
          "step's division output so a fanout child converts only its own slice.",
      ),
  }),
  outputSchema: z.object({
    convertedCount: z
      .number()
      .describe("Number of E844SD rows successfully converted to E844D"),
    skippedCount: z
      .number()
      .describe(
        "Number of E844SD rows skipped (filtered out by shouldCreateE844DRecord)",
      ),
    errorCount: z
      .number()
      .describe("Number of E844SD rows that failed during conversion"),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the updatedAfter timestamp from the input, " +
          "so downstream steps can scope queries to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .prefault(2000)
      .describe("Number of rows to process per DB batch"),
    enrichmentConcurrency: z
      .number()
      .prefault(10)
      .describe(
        "DEPRECATED — no-op. Batch enrichment handles concurrency internally.",
      ),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "90 minutes",
    retry: { maximumAttempts: 2 },
  },
});
